import {Fragment} from "react";
import {AlertTriangle, Copy, RotateCcw, Trash2, Undo2} from "lucide-react";
import {StatusBadge} from "../../../shared/components/StatusBadge";
import {isLegacyRow, normalizeText, textareaRows} from "../appTextsContracts";
import {useTranslation, i18n} from "../../../shared/i18n";


export const WarningList = ({warnings}) => (
    <ul className="content-texts-warnings">
        {warnings.map((warning, index) => (
            <li className="content-texts-warning" key={`${warning.rule || warning.code}-${warning.path || warning.key || ""}-${index}`}>
                <AlertTriangle aria-hidden="true" size={14} />
                <span>
                    {warning.message || warning.code}
                    {warning.source === "base" ? <small> {i18n.t("sections.content.p130")}</small> : null}
                    {!warning.key && warning.path ? <small> · {warning.path}</small> : null}
                </span>
            </li>
        ))}
    </ul>
);

const FieldErrors = ({messages}) => (messages?.length ? (
    <ul className="content-texts-field-errors">
        {Array.from(new Set(messages)).map((message) => <li className="content-field-error" key={message}>{message}</li>)}
    </ul>
) : null);

const valueOf = (row, draft) => (row.key in draft ? draft[row.key] : (typeof row.override === "string" ? row.override : ""));

// Changed both in this draft and on the server since the draft started (after a reload).
const ConflictBadge = ({conflict}) => (conflict ? <StatusBadge tone="danger">{i18n.t("sections.content.p131")}</StatusBadge> : null);

const RowBadges = ({mode, row, value, changed, conflict, pageExists}) => {
    const {t} = useTranslation();
    if (mode === "page") {
        return (
            <>
                {row.status === "missing" && pageExists ? <StatusBadge tone="danger">{t("sections.content.p132")}</StatusBadge> : null}
                {row.status === "extra" ? <StatusBadge tone="warning">{t("sections.content.p133")}</StatusBadge> : null}
                {changed ? <StatusBadge tone="success">{t("sections.content.p129")}</StatusBadge> : null}
                <ConflictBadge conflict={conflict} />
            </>
        );
    }
    if (isLegacyRow(row) && value === row.override) {
        // «stale»: the key left the base text; «invalid»: the key exists but the stored value is not text.
        return <StatusBadge tone="danger">{row.status === "stale" ? t("sections.content.p134") : t("sections.content.p135")}</StatusBadge>;
    }
    const own = Boolean(normalizeText(value)) && normalizeText(value) !== row.base;
    return (
        <>
            {own ? <StatusBadge tone="warning">{t("sections.content.p136")}</StatusBadge> : <StatusBadge tone="neutral">{t("sections.content.p137")}</StatusBadge>}
            {changed ? <StatusBadge tone="success">{t("sections.content.p129")}</StatusBadge> : null}
            <ConflictBadge conflict={conflict} />
        </>
    );
};

const LegacyCell = ({row, value}) => {
    const {t} = useTranslation();
    if (value === null) {
        return <p className="content-texts-note">{t("sections.content.p138")}</p>;
    }
    return (
        <div className="content-texts-legacy">
            <span>{row.override}</span>
            <small>{row.status === "stale" ? t("sections.content.p139") : t("sections.content.p140")}</small>
        </div>
    );
};

const RowActions = ({mode, row, value, canEdit, onChange}) => {
    const {t} = useTranslation();
    if (mode === "page") {
        if (row.status === "extra") {
            return value === null ? (
                <button aria-label={t("sections.content.p141", {v0: row.key})} className="admin-row-action" disabled={!canEdit} onClick={() => onChange(row.key, row.override ?? "")} type="button"><Undo2 size={14} /> {t("sections.content.p142")}</button>
            ) : (
                <button aria-label={t("sections.content.p143", {v0: row.key})} className="admin-row-action" disabled={!canEdit} onClick={() => onChange(row.key, null)} type="button"><Trash2 size={14} /> {t("sections.content.p144")}</button>
            );
        }
        return (
            <button aria-label={t("sections.content.p145", {v0: row.key})} className="admin-row-action" disabled={!canEdit || typeof row.base !== "string" || value === row.base} onClick={() => onChange(row.key, row.base)} type="button"><Copy size={14} /> {t("sections.content.p146")}</button>
        );
    }
    if (isLegacyRow(row) && (value === row.override || value === null)) {
        return value === null ? (
            <button aria-label={t("sections.content.p147", {v0: row.key})} className="admin-row-action" disabled={!canEdit} onClick={() => onChange(row.key, row.override ?? "")} type="button"><Undo2 size={14} /> {t("sections.content.p142")}</button>
        ) : (
            <button aria-label={t("sections.content.p148", {v0: row.key})} className="admin-row-action" disabled={!canEdit} onClick={() => onChange(row.key, null)} type="button"><Trash2 size={14} /> {t("sections.content.p149")}</button>
        );
    }
    return (
        <button aria-label={t("sections.content.p150", {v0: row.key})} className="admin-row-action" disabled={!canEdit || !normalizeText(value)} onClick={() => onChange(row.key, "")} type="button"><RotateCcw size={14} /> {t("sections.content.p151")}</button>
    );
};

export const AppTextsTable = ({
    mode,
    rows,
    draft,
    baseLabel,
    effectiveByKey,
    effectiveNote = null,
    canEdit,
    errorsByKey,
    warningsByKey,
    changed,
    conflicts = new Set(),
    pageExists,
    emptyMessage,
    onChange,
}) => {
    const {t} = useTranslation();
    if (!rows.length) {
        return <p className="content-texts-note">{emptyMessage || t("sections.content.p103")}</p>;
    }

    return (
        <div className="admin-table-wrap content-texts-table-wrap">
            <table className="admin-table content-texts-table">
                <thead>
                    <tr>
                        <th>{t("sections.content.p152")}</th>
                        <th>{baseLabel}</th>
                        <th>{t("sections.content.p153")}</th>
                        <th>{t("sections.content.p154")}</th>
                        <th><span className="content-texts-sr-only">{t("sections.content.p155")}</span></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const value = valueOf(row, draft);
                        const warnings = warningsByKey[row.key] || [];
                        const errors = errorsByKey[row.key] || [];
                        const effective = effectiveByKey[row.key];
                        const legacy = mode === "flat" && isLegacyRow(row) && (value === row.override || (value === null && row.status === "stale"));
                        const removedPageKey = mode === "page" && value === null;
                        return (
                            <Fragment key={row.key}>
                                <tr className={warnings.length ? "has-warnings" : undefined}>
                                    <td className="content-texts-key-cell">
                                        <code className="content-texts-mono">{row.key}</code>
                                        <span className="content-texts-row-badges">
                                            <RowBadges changed={changed.has(row.key)} conflict={conflicts.has(row.key)} mode={mode} pageExists={pageExists} row={row} value={value} />
                                        </span>
                                    </td>
                                    <td className="content-texts-base">{typeof row.base === "string" ? row.base : "—"}</td>
                                    <td className="content-texts-input-cell">
                                        {legacy ? <LegacyCell row={row} value={value} /> : removedPageKey ? (
                                            <p className="content-texts-note">{t("sections.content.p156")}</p>
                                        ) : (
                                            <textarea
                                                aria-invalid={errors.length ? "true" : undefined}
                                                aria-label={t("sections.content.p157", {v0: row.key})}
                                                className="admin-textarea content-texts-input"
                                                disabled={!canEdit}
                                                onChange={(event) => onChange(row.key, event.target.value)}
                                                placeholder={mode === "page" ? t("sections.content.p158") : t("sections.content.p159")}
                                                rows={textareaRows(value, row.base)}
                                                value={value ?? ""}
                                            />
                                        )}
                                        <FieldErrors messages={errors} />
                                    </td>
                                    <td className={effectiveNote ? "content-texts-effective is-pending" : "content-texts-effective"}>
                                        {typeof effective === "string" && effective ? effective : "—"}
                                        {effectiveNote ? <small>{effectiveNote}</small> : null}
                                    </td>
                                    <td className="content-texts-actions-cell">
                                        <RowActions canEdit={canEdit} mode={mode} onChange={onChange} row={row} value={value} />
                                    </td>
                                </tr>
                                {warnings.length ? (
                                    <tr className="content-texts-warning-row">
                                        <td colSpan={5}><WarningList warnings={warnings} /></td>
                                    </tr>
                                ) : null}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
